import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { db, collection, getDocs, query, where, orderBy, addDoc, serverTimestamp, doc, getDoc, onSnapshot } from '../../components/firebase';
import { useAuth } from '../../context/AuthContext';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Briefcase, CheckCircle, Clock, Send, Search,
    X, Grid, List, Sliders, MapPin, Globe, Users, Info, ExternalLink,
    FileText, Gift, Bell
} from 'lucide-react';
import { Linkedin, Twitter } from 'lucide-react';
import { toast } from 'react-toastify';
import { cn } from '../../lib/utils';
import JobCard from './components/JobCard';
import JobDetailModal from './components/JobDetailModal';
import AuthLockedView from '../../components/AuthLockedView';
import MyOffers from '../Auth/MyOffers';

const JOB_TYPES = ['All', 'Full-time', 'Internship', 'Part-time', 'Contract', 'Walk-in Interview', 'Assessment Drive'];
const EXP_LEVELS = ['All', 'Fresher', '0–1 Year', '1–3 Years', '3+ Years'];

const statusColors = {
    applied: { bg: 'rgba(34,197,94,0.12)', color: '#22c55e', label: 'Applied' },
    interviewed: { bg: 'rgba(234,179,8,0.12)', color: '#eab308', label: 'Interviewed' },
    selected: { bg: 'rgba(34,197,94,0.12)', color: '#22c55e', label: 'Selected' },
    rejected: { bg: 'rgba(239,68,68,0.12)', color: '#ef4444', label: 'Rejected' },
    offered: { bg: 'rgba(139,92,246,0.12)', color: '#8b5cf6', label: 'Offered' },
    pending: { bg: 'rgba(234,179,8,0.12)', color: '#eab308', label: 'Pending' },
};

const Opportunities = () => {
    const { user, userProfile } = useAuth();
    const navigate = useNavigate();
    const [jobs, setJobs] = useState([]);
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedType, setSelectedType] = useState('All');
    const [selectedWorkMode, setSelectedWorkMode] = useState('All');
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [selectedPay, setSelectedPay] = useState('All');
    const [selectedSalary, setSelectedSalary] = useState('All');
    const [selectedExp, setSelectedExp] = useState('All');
    const [selectedLocation, setSelectedLocation] = useState('');
    const [minStipend, setMinStipend] = useState(0);
    const [maxDuration, setMaxDuration] = useState('Any');
    const [showFilters, setShowFilters] = useState(false);
    const [showLoginPrompt, setShowLoginPrompt] = useState(false);
    const [pendingJob, setPendingJob] = useState(null);
    const [selectedJob, setSelectedJob] = useState(null);
    const [viewMode, setViewMode] = useState('grid');
    const [applyingJobId, setApplyingJobId] = useState(null);
    const [selectedCompanyId, setSelectedCompanyId] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [pageTab, setPageTab] = useState('opportunities');
    const [pendingOfferCount, setPendingOfferCount] = useState(0);
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        const h = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', h);
        return () => window.removeEventListener('resize', h);
    }, []);

    const fetchJobs = useCallback(async () => {
        setLoading(true);
        try {
            const q = query(collection(db, 'jobs'), where('status', '==', 'approved'), orderBy('createdAt', 'desc'));
            const snap = await getDocs(q);
            setJobs(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } catch (err) {
            console.error(err);
            try {
                const q = query(collection(db, 'jobs'), orderBy('createdAt', 'desc'));
                const snap = await getDocs(q);
                setJobs(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(j => j.status === 'approved' || j.status === 'active'));
            } catch (e) { console.error(e); }
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchApplications = useCallback(async () => {
        try {
            const q = query(collection(db, 'applications'), where('studentId', '==', user.uid));
            const snap = await getDocs(q);
            setApplications(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } catch (err) { console.error(err); }
    }, [user]);

    useEffect(() => {
        fetchJobs();
        if (user) fetchApplications();
    }, [user, fetchJobs, fetchApplications]);

    // Real-time pending offer count for badge
    useEffect(() => {
        if (!user?.uid) return;
        const q = query(collection(db, 'offers'), where('studentId', '==', user.uid), where('status', '==', 'pending'));
        const unsub = onSnapshot(q, snap => setPendingOfferCount(snap.size));
        return () => unsub();
    }, [user]);

    // Deep-link: auto-open job detail modal when ?job=<id> is in URL
    useEffect(() => {
        const jobId = searchParams.get('job');
        if (jobId && jobs.length > 0) {
            const found = jobs.find(j => j.id === jobId);
            if (found) {
                setSelectedJob(found);
                // Clean the param from URL after opening so sharing again works
                setSearchParams({}, { replace: true });
            }
        }
    }, [jobs, searchParams, setSearchParams]);

    const isProfileComplete = (profile) => {
        if (!profile) return false;
        const required = ['fullName', 'college', 'branch', 'semester', 'headline'];
        const hasRequired = required.every(field => profile[field] && String(profile[field]).trim().length > 0);
        const hasSkills = Array.isArray(profile.skills) && profile.skills.length > 0;
        return hasRequired && hasSkills;
    };

    const handleApply = async (job) => {
        console.log('handleApply triggered for job:', job.id);
        if (!user) {
            setPendingJob(job);
            setShowLoginPrompt(true);
            return;
        }

        console.log('Checking profile completeness for:', userProfile);
        if (!isProfileComplete(userProfile)) {
            console.log('Profile incomplete. Redirecting...');
            toast.warning('Please complete your profile (Bio, Skills, Headline, etc.) before applying!', { position: "top-center" });
            setTimeout(() => navigate('/profile'), 2000);
            return;
        }

        if (applications.find(a => a.jobId === job.id)) { toast.info('Already applied'); return; }

        setApplyingJobId(job.id);
        try {
            const applicationData = {
                jobId: job.id,
                studentId: user.uid,
                studentEmail: user.email,
                studentName: userProfile?.fullName || user.displayName || user.email,
                studentPhoto: userProfile?.photo || user.photoURL || null,
                companyId: job.companyId,
                companyName: job.companyName,
                jobTitle: job.title,
                status: 'applied',
                appliedAt: serverTimestamp()
            };
            await addDoc(collection(db, 'applications'), applicationData);

            await addDoc(collection(db, 'notifications'), {
                title: 'New Application',
                message: `${user.displayName || user.email} applied for "${job.title}"`,
                type: 'info',
                recipient: 'specific',
                recipientUid: job.companyId,
                timestamp: serverTimestamp(),
                createdAt: serverTimestamp(),
                readBy: []
            });

            toast.success('Application submitted!');
            fetchApplications();
        } catch (err) {
            toast.error('Failed to submit application');
        } finally {
            setApplyingJobId(null);
        }
    };

    const filteredJobs = useMemo(() => {
        return jobs.filter(job => {
            const matchSearch = !searchTerm ||
                job.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                job.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                job.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                job.category?.toLowerCase().includes(searchTerm.toLowerCase());
            const matchType = selectedType === 'All' || job.type === selectedType;
            const matchWork = selectedWorkMode === 'All' || job.workMode === selectedWorkMode;
            const matchCat = selectedCategory === 'All' || job.category === selectedCategory;

            // Pay filter: check isPaid field or if job has stipend
            const matchPay = selectedPay === 'All' ||
                (selectedPay === 'Paid' && (job.isPaid !== false && !!job.stipend)) ||
                (selectedPay === 'Unpaid' && (job.isPaid === false || !job.stipend));

            // Salary range filter — parse stipend text for approximate numbers
            const stipendNum = (() => {
                if (!job.stipend) return 0;
                const matches = job.stipend.match(/\d+[,\d]*/g);
                if (!matches) return 0;
                const num = parseInt(matches[0].replace(/,/g, ''), 10);
                // If value looks like LPA (e.g., 6 LPA), multiply to monthly
                if (job.stipend.toLowerCase().includes('lpa')) return (num * 100000) / 12;
                return num;
            })();
            const matchSalary = selectedSalary === 'All' ||
                (selectedSalary === 'Under ₹10k' && stipendNum > 0 && stipendNum < 10000) ||
                (selectedSalary === '₹10k–₹30k' && stipendNum >= 10000 && stipendNum < 30000) ||
                (selectedSalary === '₹30k–₹60k' && stipendNum >= 30000 && stipendNum < 60000) ||
                (selectedSalary === '₹60k+' && stipendNum >= 60000);

            // Experience level filter
            const matchExp = selectedExp === 'All' ||
                job.experience?.toLowerCase().includes(selectedExp.toLowerCase()) ||
                (selectedExp === 'Fresher' && (!job.experience || job.experience.toLowerCase().includes('fresher') || job.experience === '0'));

            // Advanced: Location filter
            const matchLocation = !selectedLocation ||
                job.location?.toLowerCase().includes(selectedLocation.toLowerCase()) ||
                (selectedLocation.toLowerCase() === 'remote' && job.workMode?.toLowerCase() === 'remote');

            // Advanced: Stipend filter (using already parsed stipendNum)
            const matchMinStipend = minStipend === 0 || stipendNum >= minStipend;

            // Advanced: Duration filter (for internships)
            const matchDuration = maxDuration === 'Any' ||
                (job.type === 'Internship' && parseInt(job.duration) <= parseInt(maxDuration)) ||
                job.type !== 'Internship';

            return matchSearch && matchType && matchWork && matchCat && matchPay && matchSalary && matchExp && matchLocation && matchMinStipend && matchDuration;
        });
    }, [jobs, searchTerm, selectedType, selectedWorkMode, selectedCategory, selectedPay, selectedSalary, selectedExp, selectedLocation, minStipend, maxDuration]);

    const activeFilterCount = [
        selectedType, selectedWorkMode, selectedCategory, selectedPay, selectedSalary, selectedExp,
        selectedLocation ? 'Location' : 'All',
        minStipend > 0 ? 'Stipend' : 'All',
        maxDuration !== 'Any' ? 'Duration' : 'All'
    ].filter(f => f !== 'All').length;

    const stats = useMemo(() => ([
        { label: 'Open Positions', value: jobs.length, icon: <Briefcase />, color: 'var(--primary)', bg: 'rgba(var(--primary), 0.1)' },
        { label: 'Applied', value: applications.length, icon: <Send />, color: 'var(--primary)', bg: 'rgba(var(--primary), 0.1)' },
        { label: 'In Review', value: applications.filter(a => a.status === 'applied' || a.status === 'interviewed').length, icon: <Clock />, color: 'var(--warning)', bg: 'rgba(var(--warning), 0.1)' },
        { label: 'Offers', value: applications.filter(a => a.status === 'selected' || a.status === 'offered').length, icon: <CheckCircle />, color: 'var(--secondary)', bg: 'rgba(var(--secondary), 0.1)' },
    ]), [jobs, applications]);

    const clearFilters = () => {
        setSelectedType('All');
        setSelectedWorkMode('All');
        setSelectedCategory('All');
        setSelectedPay('All');
        setSelectedSalary('All');
        setSelectedExp('All');
        setSelectedLocation('');
        setMinStipend(0);
        setMaxDuration('Any');
        setSearchTerm('');
    };

    const FilterChip = ({ label, options, value, onChange }) => (
        <div className="flex flex-col gap-2">
            <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">{label}</label>
            <div className="flex flex-wrap gap-2">
                {options.map(opt => (
                    <button
                        key={opt}
                        onClick={() => onChange(opt)}
                        className={cn(
                            "px-3.5 py-1.5 rounded-full text-[11px] font-black transition-all border",
                            value === opt
                                ? "bg-primary text-primary-foreground border-primary shadow-lg shadow-primary/20"
                                : "bg-card text-muted-foreground border-border hover:border-primary/50 hover:text-foreground"
                        )}
                    >
                        {opt}
                    </button>
                ))}
            </div>
        </div>
    );

    const FilterContent = ({ layout = 'vertical' }) => (
        <div className={cn(
            "flex flex-wrap items-start",
            layout === 'horizontal' ? "flex-row gap-6" : "flex-col gap-7"
        )}>
            {/* ROLE/LOCATION SECTION */}
            <div className={cn(
                "flex flex-col gap-3",
                layout === 'horizontal' ? "flex-[1.2_1_280px]" : "w-full"
            )}>
                <label className="text-[11px] font-black text-primary uppercase tracking-widest opacity-80">
                    {layout === 'horizontal' ? 'Search' : 'Basic Search'}
                </label>
                <div className={cn(
                    "flex gap-3",
                    layout === 'horizontal' ? "flex-row" : "flex-col"
                )}>
                    <div className="flex-1 relative">
                        <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                            type="text"
                            placeholder="Role/Profile"
                            value={selectedCategory === 'All' ? '' : selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value || 'All')}
                            className="w-full pl-10 pr-4 py-2.5 bg-accent/30 border border-border rounded-xl text-foreground text-sm font-medium outline-none focus:border-primary transition-all"
                        />
                    </div>

                    <div className="flex-1 relative">
                        <MapPin size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                            type="text"
                            placeholder="City/Remote"
                            value={selectedLocation}
                            onChange={(e) => setSelectedLocation(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 bg-accent/30 border border-border rounded-xl text-foreground text-sm font-medium outline-none focus:border-primary transition-all"
                        />
                    </div>
                </div>
                <label className="flex items-center gap-2 mt-1 cursor-pointer group">
                    <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-border text-primary focus:ring-primary/20 accent-primary"
                        checked={selectedWorkMode === 'Remote'}
                        onChange={(e) => setSelectedWorkMode(e.target.checked ? 'Remote' : 'All')}
                    />
                    <span className="text-xs font-bold text-muted-foreground group-hover:text-foreground transition-colors uppercase tracking-widest">Remote Only</span>
                </label>
            </div>

            {layout !== 'horizontal' && <div className="h-px bg-border/50 w-full" />}

            {/* STIPEND SECTION */}
            <div className={cn(
                "flex flex-col gap-3",
                layout === 'horizontal' ? "flex-[1_1_300px]" : "w-full"
            )}>
                <label className="text-[11px] font-black text-primary uppercase tracking-widest opacity-80">Stipend (Min)</label>
                <div className="flex gap-4 items-center mb-1">
                    <div className="relative w-24">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">₹</span>
                        <input
                            type="number"
                            value={minStipend}
                            onChange={(e) => setMinStipend(Math.max(0, parseInt(e.target.value) || 0))}
                            className="w-full pl-6 pr-2 py-1.5 bg-accent/30 border border-border rounded-lg text-foreground text-xs font-black outline-none focus:border-primary"
                        />
                    </div>
                    <div className="flex-1">
                        <input
                            type="range"
                            min="0"
                            max="100000"
                            step="1000"
                            value={minStipend}
                            onChange={(e) => setMinStipend(parseInt(e.target.value))}
                            className="w-full h-1.5 bg-accent rounded-lg appearance-none cursor-pointer accent-primary"
                        />
                    </div>
                </div>
                <div className="flex justify-between items-center px-1">
                    <span className="text-[10px] text-muted-foreground font-black uppercase tracking-widest">₹0</span>
                    <span className="text-xs text-primary font-black uppercase tracking-widest">
                        {minStipend >= 1000
                            ? `₹${(minStipend / 1000).toFixed(minStipend % 1000 === 0 ? 0 : 1)}k`
                            : `₹${minStipend}`
                        }
                    </span>
                    <span className="text-[10px] text-muted-foreground font-black uppercase tracking-widest">₹1L</span>
                </div>
            </div>

            {layout !== 'horizontal' && <div className="h-px bg-border/50 w-full" />}

            {/* OPTIONS SECTION */}
            <div className={cn(
                "flex gap-6",
                layout === 'horizontal' ? "flex-[1.5_1_300px]" : "flex-col w-full"
            )}>
                <div className="flex-1 min-w-[140px]">
                    <FilterChip
                        label="Job Type"
                        options={['All', 'Internship', 'Full-time']}
                        value={selectedType}
                        onChange={setSelectedType}
                    />
                </div>
                {selectedType === 'Internship' && (
                    <div className="flex-1 min-w-[140px]">
                        <FilterChip
                            label="Duration"
                            options={['Any', '1', '3', '6', '12']}
                            value={maxDuration}
                            onChange={setMaxDuration}
                        />
                    </div>
                )}
                <div className="flex-1 min-w-[140px]">
                    <FilterChip
                        label="Experience"
                        options={EXP_LEVELS}
                        value={selectedExp}
                        onChange={setSelectedExp}
                    />
                </div>
            </div>
        </div>
    );

    return (
        <div className="job-portal-container relative min-h-screen">
            {!user && <AuthLockedView />}
            <div className={cn(
                "max-w-[1400px] mx-auto px-4 sm:px-6 transition-all duration-500",
                !user && "blur-md pointer-events-none opacity-60 select-none"
            )}>
                {/* Hero Header */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8 md:mb-12 pt-10">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 mb-4">
                        <Briefcase size={11} className="text-primary" />
                        <span className="text-[10px] font-black text-primary uppercase tracking-widest">Career Hub</span>
                    </div>
                    <h1 className="text-3xl md:text-5xl lg:text-6xl font-black text-foreground mb-3 tracking-tighter leading-[1.1]">
                        Find Your Next <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">Opportunity</span>
                    </h1>
                    {!isMobile && (
                        <p className="text-muted-foreground text-sm md:text-base max-w-xl font-medium leading-relaxed">
                            Discover internships, full-time roles, and more from top companies.
                        </p>
                    )}
                </motion.div>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8">
                    {stats.map((stat, i) => (
                        <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                            className="p-4 bg-card border border-border rounded-2xl shadow-sm hover:shadow-md transition-shadow group">
                            <div className="flex items-center gap-2.5 mb-2.5">
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors" style={{ backgroundColor: stat.bg, color: stat.color }}>
                                    {React.cloneElement(stat.icon, { size: 16 })}
                                </div>
                                <span className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">{stat.label}</span>
                            </div>
                            <div className="text-2xl font-black text-foreground tabular-nums tracking-tight">{stat.value}</div>
                        </motion.div>
                    ))}
                </div>

                {/* ── Page Tabs ─────────────────────────────────────────────── */}
                <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar mb-8">
                    {[
                        { id: 'opportunities', label: 'Opportunities', icon: <Briefcase size={14} /> },
                        { id: 'applied', label: 'Applied', icon: <Send size={14} /> },
                        { id: 'status', label: 'Application Status', icon: <FileText size={14} /> },
                        { id: 'offers', label: 'Offers from Recruiter', icon: <Gift size={14} />, badge: pendingOfferCount },
                    ].map(tab => (
                        <motion.button
                            key={tab.id}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.97 }}
                            onClick={() => setPageTab(tab.id)}
                            className={cn(
                                'flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest border transition-all',
                                pageTab === tab.id
                                    ? 'bg-primary text-primary-foreground border-primary shadow-lg shadow-primary/20'
                                    : 'bg-card text-muted-foreground border-border hover:border-primary/50 hover:text-foreground'
                            )}
                        >
                            {tab.icon} {tab.label}
                            {tab.badge > 0 && (
                                <span className="min-w-[18px] h-[18px] rounded-full bg-white/20 flex items-center justify-center text-[10px] font-black px-1">
                                    {tab.badge}
                                </span>
                            )}
                        </motion.button>
                    ))}
                </div>

                <div className="sticky top-[64px] z-[50] bg-background/80 backdrop-blur-xl border-b border-border -mx-4 sm:-mx-6 px-4 sm:px-6 py-4 mb-8">
                    {/* Search Row */}
                    <div className="flex gap-3 mb-4 items-center">
                        <div className="flex-1 relative">
                            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                            <input
                                type="text"
                                placeholder="Search jobs, companies, skills..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-10 py-2.5 bg-accent/20 border border-border rounded-xl text-foreground text-sm font-medium outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                            />
                            {searchTerm && (
                                <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1">
                                    <X size={16} />
                                </button>
                            )}
                        </div>

                        <motion.button
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setShowFilters(!showFilters)}
                            className={cn(
                                "group flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 font-black transition-all text-xs uppercase tracking-widest",
                                activeFilterCount > 0
                                    ? "bg-primary text-primary-foreground border-primary shadow-lg shadow-primary/20"
                                    : "bg-card text-muted-foreground border-border hover:border-primary/50 hover:text-foreground"
                            )}>
                            <Sliders size={15} />
                            {!isMobile && <span>Filters</span>}
                            {activeFilterCount > 0 && (
                                <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-[10px] font-black">
                                    {activeFilterCount}
                                </span>
                            )}
                        </motion.button>

                        {/* View Mode Toggle - hidden on mobile (always grid) */}
                        {!isMobile && (
                            <div className="flex bg-accent/30 rounded-xl p-1 border border-border shrink-0">
                                {[
                                    { mode: 'grid', icon: <Grid size={15} /> },
                                    { mode: 'list', icon: <List size={15} /> }
                                ].map(v => (
                                    <button
                                        key={v.mode}
                                        onClick={() => setViewMode(v.mode)}
                                        className={cn(
                                            "px-4 py-2 rounded-lg transition-all",
                                            viewMode === v.mode ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                                        )}
                                    >
                                        {v.icon}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* ── Horizontal Scrollable Type Tabs ─── */}
                    <div className="flex gap-2.5 overflow-x-auto pb-1 scrollbar-none no-scrollbar">
                        {JOB_TYPES.map(t => (
                            <button
                                key={t}
                                onClick={() => setSelectedType(t)}
                                className={cn(
                                    "flex-shrink-0 px-4 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest border transition-all whitespace-nowrap",
                                    selectedType === t
                                        ? "bg-primary text-primary-foreground border-primary shadow-lg shadow-primary/20"
                                        : "bg-card text-muted-foreground border-border hover:border-primary/50 hover:text-foreground"
                                )}
                            >
                                {t}
                            </button>
                        ))}
                    </div>

                    {/* ── Mobile Filter Drawer ───────── */}
                    <AnimatePresence>
                        {showFilters && isMobile && (
                            <div className="fixed inset-0 z-[10000] flex flex-col">
                                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowFilters(false)}
                                    className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
                                <motion.div
                                    initial={{ y: '100%' }}
                                    animate={{ y: 0 }}
                                    exit={{ y: '100%' }}
                                    transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                                    className="absolute bottom-0 left-0 right-0 bg-background border-t border-border rounded-t-3xl p-6 pb-12 max-h-[85vh] overflow-y-auto"
                                >
                                    <div className="flex justify-between items-center mb-6">
                                        <h3 className="text-lg font-black uppercase tracking-widest text-foreground">Filters</h3>
                                        <div className="flex gap-4 items-center">
                                            {activeFilterCount > 0 && (
                                                <button onClick={clearFilters} className="text-primary font-black text-xs uppercase tracking-widest px-3 py-1 hover:bg-primary/10 rounded-full transition-all">Clear All</button>
                                            )}
                                            <button onClick={() => setShowFilters(false)} className="bg-accent/50 p-2 rounded-full hover:bg-accent transition-colors"><X size={20} /></button>
                                        </div>
                                    </div>
                                    <FilterContent />
                                    <button
                                        onClick={() => setShowFilters(false)}
                                        className="w-full mt-8 py-4 rounded-xl bg-primary text-primary-foreground text-sm font-black uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all"
                                    >
                                        Show {filteredJobs.length} results
                                    </button>
                                </motion.div>
                            </div>
                        )}
                    </AnimatePresence>

                    {/* ── Opportunities Tab Content ───────── */}
                    {pageTab === 'opportunities' && (
                        <div className="flex flex-col gap-8">
                            {/* Top Filters Bar */}
                            {!isMobile && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="bg-card border border-border rounded-3xl p-6 shadow-sm"
                                >
                                    <div className="flex justify-between items-center mb-6">
                                        <div className="flex items-center gap-3">
                                            <div className="w-1 h-5 bg-primary rounded-full" />
                                            <h3 className="text-xs font-black text-foreground uppercase tracking-[0.2em]">Filter Engine</h3>
                                        </div>
                                        {activeFilterCount > 0 && (
                                            <button onClick={clearFilters} className="text-[10px] font-black text-primary uppercase tracking-widest px-4 py-2 bg-primary/5 border border-primary/20 rounded-full hover:bg-primary/10 transition-all">Reset All Parameters</button>
                                        )}
                                    </div>
                                    <FilterContent layout="horizontal" />
                                </motion.div>
                            )}

                            {/* Content Area */}
                            <div className="flex flex-col gap-6">
                                {/* Results Count Header */}
                                <div className="flex justify-between items-end px-1">
                                    <div>
                                        <h2 className="text-xl md:text-2xl font-black text-foreground tracking-tight m-0">
                                            Available <span className="text-primary">Roles</span>
                                        </h2>
                                        <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mt-1 opacity-60">
                                            {filteredJobs.length} opportunit{filteredJobs.length !== 1 ? 'ies' : 'y'} matched
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        {activeFilterCount > 0 && (
                                            <button onClick={clearFilters} className="flex items-center gap-2 group">
                                                <X size={12} className="text-destructive transition-transform group-hover:rotate-90" />
                                                <span className="text-[10px] font-black text-destructive uppercase tracking-widest">Clear Filters</span>
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Jobs Display Grid */}
                                {loading ? (
                                    <div className={cn(
                                        "grid gap-4",
                                        isMobile ? "grid-cols-1" : (viewMode === 'grid' ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3" : "grid-cols-1")
                                    )}>
                                        {[1, 2, 3, 4, 5, 6].map(i => (
                                            <div key={i} className="h-[180px] bg-card/40 border border-border rounded-3xl animate-pulse flex flex-col p-6 gap-4">
                                                <div className="w-12 h-12 bg-accent rounded-xl" />
                                                <div className="w-2/3 h-4 bg-accent rounded-full" />
                                                <div className="w-1/3 h-3 bg-accent rounded-full opacity-50" />
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    filteredJobs.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-20 px-6 bg-card/30 border-2 border-dashed border-border rounded-3xl text-center">
                                            <div className="w-16 h-16 bg-accent rounded-2xl flex items-center justify-center mb-6 text-muted-foreground">
                                                <Briefcase size={32} />
                                            </div>
                                            <h3 className="text-lg font-black text-foreground uppercase tracking-widest mb-2">No matching roles found</h3>
                                            <p className="text-sm text-muted-foreground font-medium max-w-sm mb-8">Try adjusting your filters or search keywords to find more opportunities.</p>
                                            {activeFilterCount > 0 && (
                                                <button onClick={clearFilters} className="px-8 py-3 bg-primary text-primary-foreground rounded-xl text-xs font-black uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-105 active:scale-95 transition-all">Clear All Filters</button>
                                            )}
                                        </div>
                                    ) : (
                                        <div className={cn(
                                            "grid gap-4 pb-12",
                                            isMobile ? "grid-cols-1" : (viewMode === 'grid' ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3" : "grid-cols-1")
                                        )}>
                                            {filteredJobs.map((job) => {
                                                const application = applications.find(a => a.jobId === job.id);
                                                return (
                                                    <JobCard
                                                        key={job.id} job={job}
                                                        onViewDetails={setSelectedJob}
                                                        onViewCompany={setSelectedCompanyId}
                                                        onApply={handleApply}
                                                        isApplied={!!application}
                                                        appStatus={statusColors[application?.status]}
                                                        viewMode={isMobile ? 'grid' : viewMode}
                                                    />
                                                );
                                            })}
                                        </div>
                                    ))
                                }
                            </div>
                        </div>
                    )} {/* end pageTab === 'opportunities' */}

                    {/* ── Applied Tab ─────────────────────────────────────── */}
                    {pageTab === 'applied' && (
                        <div className="flex flex-col gap-4 pb-12">
                            <h2 className="text-xl font-black text-foreground tracking-tight">
                                Your Applications <span className="text-primary">({applications.length})</span>
                            </h2>
                            {applications.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-20 bg-card/30 border-2 border-dashed border-border rounded-3xl text-center">
                                    <Send size={36} className="text-muted-foreground opacity-30 mb-4" />
                                    <p className="font-black text-foreground uppercase tracking-widest mb-2">No applications yet</p>
                                    <p className="text-sm text-muted-foreground">Apply to jobs and they'll appear here.</p>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-3">
                                    {applications.map((app, i) => {
                                        const s = statusColors[app.status] || statusColors.applied;
                                        return (
                                            <motion.div key={app.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                                                className="p-5 bg-card border border-border rounded-2xl flex items-center justify-between gap-4 flex-wrap hover:border-primary/30 transition-colors">
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                        <Briefcase size={18} className="text-primary" />
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="font-black text-foreground truncate">{app.jobTitle || 'Position'}</div>
                                                        <div className="text-xs text-muted-foreground">{app.companyName} &bull; {app.appliedAt?.toDate ? app.appliedAt.toDate().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Recently'}</div>
                                                    </div>
                                                </div>
                                                <span className="text-xs font-black px-3 py-1.5 rounded-full flex-shrink-0" style={{ background: s.bg, color: s.color }}>{s.label}</span>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Application Status Tab ──────────────────────────── */}
                    {pageTab === 'status' && (
                        <div className="flex flex-col gap-4 pb-12">
                            <h2 className="text-xl font-black text-foreground tracking-tight">Application <span className="text-primary">Status</span></h2>
                            {applications.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-20 bg-card/30 border-2 border-dashed border-border rounded-3xl text-center">
                                    <Clock size={36} className="text-muted-foreground opacity-30 mb-4" />
                                    <p className="font-black text-foreground uppercase tracking-widest mb-2">No applications yet</p>
                                    <p className="text-sm text-muted-foreground">Your application statuses will be tracked here.</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {applications.map((app, i) => {
                                        const s = statusColors[app.status] || statusColors.applied;
                                        const steps = ['applied', 'interviewed', 'selected'];
                                        const currentIdx = steps.indexOf(app.status);
                                        return (
                                            <motion.div key={app.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                                                className="p-5 bg-card border border-border rounded-2xl flex flex-col gap-3">
                                                <div className="flex items-start justify-between gap-2">
                                                    <div>
                                                        <div className="font-black text-foreground">{app.jobTitle || 'Position'}</div>
                                                        <div className="text-xs text-muted-foreground mt-0.5">{app.companyName}</div>
                                                    </div>
                                                    <span className="text-[10px] font-black px-2.5 py-1 rounded-full flex-shrink-0" style={{ background: s.bg, color: s.color }}>{s.label}</span>
                                                </div>
                                                {app.status !== 'rejected' ? (
                                                    <div className="flex items-center gap-1">
                                                        {steps.map((step, idx) => (
                                                            <div key={step} className={cn('h-1.5 rounded-full flex-1 transition-all', idx <= currentIdx ? 'bg-primary' : 'bg-border')} />
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="text-xs font-bold" style={{ color: '#ef4444' }}>Unfortunately rejected. Keep applying — more opportunities ahead!</p>
                                                )}
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Offers from Recruiter Tab ───────────────────────── */}
                    {pageTab === 'offers' && (
                        <div className="flex flex-col gap-4 pb-12">
                            <h2 className="text-xl font-black text-foreground tracking-tight">Offers from <span className="text-primary">Recruiters</span></h2>
                            <MyOffers />
                        </div>
                    )}

                </div>
            </div>

            {/* Job Detail Modal */}
            <JobDetailModal
                show={!!selectedJob} onClose={() => setSelectedJob(null)} job={selectedJob}
                onApply={handleApply}
                isApplied={!!applications.find(a => a.jobId === selectedJob?.id)}
                appStatus={(() => {
                    const app = applications.find(a => a.jobId === selectedJob?.id);
                    return app ? statusColors[app.status] || statusColors.applied : null;
                })()}
                isApplying={applyingJobId === selectedJob?.id}
            />

            {/* Company Detail Modal */}
            <CompanyDetailModal companyId={selectedCompanyId} onClose={() => setSelectedCompanyId(null)} />

            {/* Login Prompt Modal */}
            <LoginPromptModal
                show={showLoginPrompt}
                onClose={() => setShowLoginPrompt(false)}
                job={pendingJob}
            />
        </div>
    );
};

// --- Login Prompt Modal ---
const LoginPromptModal = ({ show, onClose, job }) => {
    const navigate = useNavigate();
    const location = useLocation();

    if (!show) return null;

    const handleAction = (path) => {
        // Encode the current job in the redirect URL to ensure it auto-opens after login
        const currentUrl = `${location.pathname}?job=${job?.id}`;
        navigate(path, { state: { from: currentUrl } });
        onClose();
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    className="absolute inset-0 bg-black/80 backdrop-blur-xl"
                />
                <motion.div
                    initial={{ opacity: 0, scale: 0.9, y: 30 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: 30 }}
                    className="relative w-full max-w-[400px] bg-card border border-border rounded-[2.5rem] p-8 text-center shadow-2xl overflow-hidden"
                >
                    {/* Decorative Background */}
                    <div className="absolute top-0 left-0 w-full h-2 bg-primary" />
                    <div className="absolute -top-24 -right-24 w-48 h-48 bg-primary/10 blur-3xl rounded-full" />

                    <div className="w-20 h-20 bg-primary/10 rounded-3xl flex items-center justify-center text-primary mx-auto mb-6 rotate-12 group-hover:rotate-0 transition-transform">
                        <Briefcase size={40} />
                    </div>

                    <h2 className="text-2xl font-black text-foreground mb-3 tracking-tight">Ready to Apply?</h2>
                    <p className="text-muted-foreground text-sm font-medium leading-relaxed mb-8">
                        Sign in or create an account to submit your application for <span className="text-foreground font-black">"{job?.title}"</span>.
                    </p>

                    <div className="flex flex-col gap-3">
                        <button
                            onClick={() => handleAction('/login')}
                            className="w-full py-4 rounded-2xl bg-primary text-primary-foreground font-black uppercase tracking-widest text-xs shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all"
                        >
                            Sign In to Account
                        </button>
                        <button
                            onClick={() => handleAction('/signup')}
                            className="w-full py-4 rounded-2xl bg-secondary text-foreground font-black uppercase tracking-widest text-xs border border-border hover:bg-accent transition-all"
                        >
                            Create New Profile
                        </button>
                    </div>

                    <button
                        onClick={onClose}
                        className="mt-6 text-[10px] font-black text-muted-foreground uppercase tracking-widest hover:text-foreground transition-colors"
                    >
                        Maybe Later
                    </button>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

// --- Modern Company Detail Modal ---
const CompanyDetailModal = ({ companyId, onClose }) => {
    const [company, setCompany] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (companyId) {
            setLoading(true);
            const fetchCompany = async () => {
                try {
                    const snap = await getDoc(doc(db, 'Companies', companyId));
                    if (snap.exists()) {
                        setCompany(snap.data());
                    } else {
                        const userSnap = await getDoc(doc(db, 'Users', companyId));
                        if (userSnap.exists()) {
                            setCompany(userSnap.data());
                        }
                    }
                } catch (e) { console.error(e); }
                setLoading(false);
            };
            fetchCompany();
        } else {
            setCompany(null);
        }
    }, [companyId]);

    if (!companyId) return null;

    return (
        <AnimatePresence>
            {companyId && (
                <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="absolute inset-0 bg-black/70 backdrop-blur-md" />
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        className="w-full max-w-[500px] bg-background border border-border rounded-[2rem] relative overflow-hidden shadow-2xl"
                    >
                        {/* Header Image/Pattern */}
                        <div className="h-[120px] bg-gradient-to-br from-primary to-secondary opacity-20" />

                        {/* Content */}
                        <div className="px-8 pb-8 -mt-10">
                            <div className="relative mb-5">
                                <div className="w-20 h-20 rounded-2xl bg-card border-[3px] border-background overflow-hidden flex items-center justify-center shadow-xl">
                                    {company?.photo ? (
                                        <img src={company.photo} alt="logo" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="text-3xl font-black text-primary uppercase">
                                            {company?.companyName?.charAt(0) || company?.fullName?.charAt(0) || '?'}
                                        </div>
                                    )}
                                </div>
                                <button onClick={onClose} className="absolute right-0 top-12 bg-accent/50 p-2 rounded-full hover:bg-accent transition-colors"><X size={18} /></button>
                            </div>

                            {loading ? (
                                <div className="py-10 text-center text-muted-foreground font-bold uppercase tracking-widest text-xs">Loading company intelligence...</div>
                            ) : (
                                <>
                                    <h2 className="text-2xl font-black text-foreground mb-1 tracking-tight">{company?.companyName || company?.fullName || 'Anonymous Company'}</h2>
                                    <p className="text-xs font-black text-primary uppercase tracking-widest opacity-80 mb-6">Established Partner</p>

                                    <div className="grid grid-cols-2 gap-3 mb-6">
                                        {[
                                            { icon: <MapPin size={12} />, label: 'Location', value: company?.location || 'Global' },
                                            { icon: <Users size={12} />, label: 'Size', value: company?.companySize || 'Private' },
                                            { icon: <Globe size={12} />, label: 'Website', value: company?.website, isLink: true },
                                        ].map((item, i) => (
                                            <div key={i} className="p-3 bg-accent/30 border border-border/50 rounded-2xl">
                                                <div className="flex items-center gap-1.5 text-primary text-[10px] font-black uppercase tracking-widest mb-1">
                                                    {item.icon} {item.label}
                                                </div>
                                                <div className="text-xs font-bold text-foreground truncate">
                                                    {item.isLink && item.value ? (
                                                        <a href={item.value.startsWith('http') ? item.value : `https://${item.value}`} target="_blank" rel="noopener noreferrer" className="text-foreground hover:text-primary transition-colors flex items-center gap-1">
                                                            Visit <ExternalLink size={10} />
                                                        </a>
                                                    ) : (item.value || 'N/A')}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {company?.bio && (
                                        <div className="mb-6">
                                            <h4 className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-2 opacity-60">About Company</h4>
                                            <p className="text-sm text-foreground/80 font-medium leading-relaxed">{company.bio}</p>
                                        </div>
                                    )}

                                    <div className="flex gap-3">
                                        {company?.linkedin && (
                                            <a href={company.linkedin} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 h-11 bg-[#0077b5] text-white rounded-xl text-xs font-black uppercase tracking-widest hover:brightness-110 transition-all">
                                                <Linkedin size={14} /> LinkedIn
                                            </a>
                                        )}
                                        {company?.twitter && (
                                            <a href={company.twitter} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 h-11 bg-foreground text-background rounded-xl text-xs font-black uppercase tracking-widest hover:brightness-110 transition-all">
                                                <Twitter size={14} /> Twitter / X
                                            </a>
                                        )}
                                    </div>

                                    {!company?.linkedin && !company?.twitter && (
                                        <div className="p-3 rounded-xl bg-amber-500/5 border border-amber-500/10 flex items-center gap-3 mt-3">
                                            <Info size={16} className="text-amber-500" />
                                            <span className="text-[10px] text-amber-500 font-black uppercase tracking-widest">Privacy: Contact details protected.</span>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default Opportunities;
